<template>
    <div class="mb-2">
        <template v-for="(row, index) in rows" :key="index">
            <v-row
                :dense="true"
                :no-gutters="true"
                align="start"
                justify="space-between"
            >
                <v-col cols="11" class="mt-4">
                    <v-row :dense="true" :no-gutters="false" class="mb-0">
                        <v-col :cols="showOperatorColumn ? 12 : 'auto'" lg="4">
                            <v-select
                                :items="properties"
                                v-model="row.property"
                                :label="$t('labels.property')"
                                color="primary"
                                variant="outlined"
                                density="compact"
                                :hide-details="true"
                            ></v-select>
                        </v-col>
                        <v-col cols="5" lg="3" v-if="showOperatorColumn">
                            <v-select
                                :items="operators"
                                v-model="row.operator"
                                :label="$t('labels.operator')"
                                color="primary"
                                variant="outlined"
                                density="compact"
                                :hide-details="true"
                            ></v-select>
                        </v-col>
                        <v-col
                            :cols="showOperatorColumn ? 7 : 6"
                            lg="5"
                            v-if="showValueColumn && row.operator !== 'exists'"
                        >
                            <v-text-field
                                v-model="row.value"
                                :label="$t('labels.value')"
                                color="primary"
                                variant="outlined"
                                density="compact"
                                :hide-details="true"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                </v-col>
                <v-col cols="1" class="mt-4 d-flex justify-center align-start">
                    <v-icon
                        @click="handleIconClick(row.complete, index)"
                        :color="row.complete ? 'secondary' : 'grey'"
                        :class="{ 'not-allowed': !row.complete }"
                        class="mt-2"
                    >
                        mdi-delete
                    </v-icon>
                </v-col>

                <v-col
                    cols="12"
                    lg="3"
                    v-if="
                        row.complete &&
                        index < rows.length - 1 &&
                        !andOnlyOperator
                    "
                    justify="start"
                    class="mt-4"
                >
                    <v-select
                        :items="['AND', 'OR']"
                        v-model="row.logic"
                        :label="$t('labels.logic')"
                        color="primary"
                        variant="outlined"
                        density="compact"
                        :hide-details="true"
                        class="text-primary"
                    ></v-select>
                </v-col>
            </v-row>
        </template>
    </div>
</template>

<script>
import { inject, ref, computed, watch } from "vue";
import { getUniqueKeys } from "@/mixins/common-utils.js";

export default {
    props: ["dynamicParams", "data", "operatorId"],
    setup(props) {
        const formData = inject("formData");
        // Identifies operators that always combine conditions with AND and hide the Logic selector.
        const andOnlyOperator = computed(() =>
            [
                "vme2.shape.Operator.rename_v3", //Rename Properties operator
                "vme2.shape.Operator.filterobject_v3", //Pick Properties operator
            ].includes(props.operatorId),
        );
        const blockParams = JSON.parse(JSON.stringify(formData.parameters));
        const rows = ref(generateRows(blockParams));
        const operators = [
            "=",
            "!=",
            "<",
            ">",
            "<=",
            ">=",
            "includes",
            "not includes",
            "matches",
            "exists",
        ];

        const showOperatorColumn = computed(
            () => props.dynamicParams.showOperatorColumn,
        );
        const showValueColumn = computed(
            () => props.dynamicParams.showValueColumn,
        );
        const inputContent = computed(() => props.data.content); //'content' is defined as prop inside InputBlock component slot

        const properties = computed(() => {
            const content = inputContent.value;

            if (!content) {
                return props.dynamicParams.propertyOptions;
            }
            if (Array.isArray(content)) {
                if (content.every((item) => typeof item === "string")) {
                    return content.map((_, index) => String(index));
                }
                return getUniqueKeys(content);
            }
            return getUniqueKeys(content);
        });

        // Watch for changes in properties
        watch(properties, (newProperties) => {
            console.log("props.data...2... ", props.data);
            // Iterate over each object in the dynamicParams of the Operator
            for (let param of formData.dynamicParams) {
                // Check the blockId with the same name of this component
                if (param.blockId == props.dynamicParams.blockId) {
                    param.propertyOptions = newProperties;
                    // Return the parameter
                    return param;
                }
            }
        });

        watch(
            rows,
            () => {
                let transformedRows = [];
                rows.value.forEach((row, index) => {
                    if (
                        row.property &&
                        (!showOperatorColumn.value || row.operator) &&
                        (!showValueColumn.value ||
                            row.operator === "exists" ||
                            row.value)
                    ) {
                        row.complete = true;
                        transformedRows.push(...generateObjects(row, index));
                        if (!rows.value[index + 1]) {
                            rows.value.push({
                                property: null,
                                operator: null,
                                value: "",
                                complete: false,
                            });
                        }
                    }
                });
                if (transformedRows.length > 0) {
                    // First, remove existing objects with the same name
                    formData.parameters = formData.parameters.filter((item) => {
                        return (
                            !item.name.startsWith("__key__") &&
                            !item.name.startsWith("__operator_") &&
                            !item.name.startsWith("__item_") &&
                            !item.name.startsWith("__logic_")
                        );
                    });
                    // Merge transformedRows with the existing data in formData.parameters
                    let combined = [...formData.parameters, ...transformedRows];
                    // Create a Set to store the string versions of the objects
                    let set = new Set();
                    // Filter duplicates
                    formData.parameters = combined.filter((item) => {
                        // Convert the object to a string
                        let key = JSON.stringify(item);
                        // Check if the string is already in the Set
                        if (!set.has(key)) {
                            // If it's not in the Set, add it and keep the object in the array
                            set.add(key);
                            return true;
                        }
                        // If the string is already in the Set, remove the object from the array
                        return false;
                    });
                }
            },
            { deep: true },
        );

        function handleIconClick(isComplete, index) {
            if (isComplete) {
                this.removeRow(index);
            }
        }

        function removeRow(index) {
            rows.value.splice(index, 1);
        }

        /**
         * This method generates an array of objects based on the completed rows in the component.
         * Each object corresponds to the property, operator, and value from that row.
         * The 'property' is always included in the objects as it is always present in the rows.
         * The 'operator' and 'value' are included only if their respective columns are shown and have been filled out by the user.
         * The method filters out any rows that are not marked as complete.
         */
        function generateObjects(row, index) {
            let objects = [];
            if (row.complete) {
                objects.push({
                    name: `__key__${index}`,
                    property: row.property,
                    bind: true,
                    custom: true,
                });
                if (showOperatorColumn.value && row.operator) {
                    objects.push({
                        name: `__operator_${index}`,
                        property: row.operator,
                        bind: true,
                        custom: true,
                    });
                }
                if (showValueColumn.value && row.value) {
                    objects.push({
                        name: `__item_${index}`,
                        property: row.value,
                        bind: true,
                        custom: true,
                    });
                }
                // connettore verso la riga successiva, solo se anche quella è compilata
                const nextRow = rows.value[index + 1];
                if (nextRow && nextRow.complete) {
                    objects.push({
                        name: `__logic_${index}_${index + 1}`,
                        property: andOnlyOperator.value
                            ? "AND"
                            : row.logic || "AND",
                        bind: true,
                        custom: true,
                    });
                }
            }
            return objects;
        }

        /**
         * This function takes an array of objects and generates a corresponding array of 'row' objects.
         * Each 'row' object corresponds to a group of objects in the input array that have the same index.
         * The 'property', 'operator', and 'value' are extracted from each group of objects and used to create the 'row' object.
         * All generated 'row' objects are marked as complete.
         */
        function generateRows(objects) {
            if (!Array.isArray(objects)) {
                console.error("Error: objects must be an array.");
                return;
            }
            if (objects.length === 0) {
                return [
                    {
                        property: null,
                        operator: null,
                        value: "",
                        complete: false,
                    },
                ];
            }

            let groups = {};
            let logicByIndex = {};

            objects.forEach((obj) => {
                const logicMatch = obj.name.match(/^__logic_(\d+)_(\d+)$/);
                if (logicMatch) {
                    logicByIndex[logicMatch[1]] = obj.property; // associata alla riga "di partenza"
                    return;
                }
                let index = obj.name.split("_").reverse()[0];
                if (!groups[index]) {
                    groups[index] = [];
                }
                groups[index].push(obj);
            });

            let rows = Object.values(groups).map((group, i) => {
                let row = {
                    property: null,
                    operator: null,
                    value: "",
                    complete: true,
                    logic: logicByIndex[i],
                };
                group.forEach((obj) => {
                    if (obj.name.startsWith("__key__")) {
                        row.property = obj.property;
                    } else if (obj.name.startsWith("__operator_")) {
                        row.operator = obj.property;
                    } else if (obj.name.startsWith("__item_")) {
                        row.value = obj.property;
                    }
                });
                return row;
            });

            let lastRow = rows[rows.length - 1];
            if (
                lastRow.property === null &&
                lastRow.operator === null &&
                lastRow.value === ""
            ) {
                lastRow.complete = false;
                delete lastRow.logic;
            }
            return rows;
        }

        return {
            rows,
            operators,
            showOperatorColumn,
            showValueColumn,
            properties,
            removeRow,
            generateObjects,
            handleIconClick,
            andOnlyOperator,
        };
    },
};
</script>

<style scoped>
.not-allowed {
    cursor: default;
}
</style>
